import { memo, useMemo } from "react";
import Chart from "react-apexcharts";

import "./OperatorProductionChart.scss";
import Loader from "../../../../../../common/Loader";

const safeNumber = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
};

const cleanName = (name = "") =>
  String(name).replace(/\s+/g, " ").trim() || "Unknown";

const compactNumber = (value) =>
  new Intl.NumberFormat("en-IN", {
    notation: "compact",
    maximumFractionDigits: 1,
  }).format(safeNumber(value));

function OperatorProductionChart({
  operators = [],
  chartKey = "default",
  loading = false,
}) {
  const chartData = useMemo(() => {
    const operatorList = Array.isArray(operators)
      ? operators.filter(Boolean)
      : [];

    return {
      categories: operatorList.map((operator) =>
        cleanName(operator?.operatorUserName),
      ),
      produced: operatorList.map((operator) =>
        safeNumber(operator?.totalProduced),
      ),
    };
  }, [operators]);

  const series = useMemo(
    () => [
      {
        name: "Produced",
        data: chartData.produced,
      },
    ],
    [chartData.produced],
  );

  const chartHeight = useMemo(
    () => Math.max(360, Math.min(820, chartData.categories.length * 44 + 90)),
    [chartData.categories.length],
  );

  const animationKey = useMemo(
    () =>
      `${chartKey}-${chartData.categories.length}-${chartData.produced.join(
        "-",
      )}`,
    [chartKey, chartData.categories.length, chartData.produced],
  );

  const options = useMemo(
    () => ({
      chart: {
        id: `operator-production-${chartKey}`,
        type: "bar",
        toolbar: {
          show: false,
        },
        zoom: {
          enabled: false,
        },

        animations: {
          enabled: true,
          easing: "easeinout",
          speed: 900,
          animateGradually: {
            enabled: true,
            delay: 120,
          },
          dynamicAnimation: {
            enabled: true,
            speed: 500,
          },
        },

        redrawOnParentResize: false,
        redrawOnWindowResize: false,
      },

      colors: ["#2f539d"],

      plotOptions: {
        bar: {
          horizontal: true,
          borderRadius: 6,
          barHeight: "56%",
          distributed: false,
          // dataLabels: {
          //   position: "top",
          // },
        },
      },

      states: {
        hover: {
          filter: {
            type: "lighten",
            value: 0.08,
          },
        },
        active: {
          filter: {
            type: "darken",
            value: 0.08,
          },
        },
      },

      // dataLabels: {
      //   enabled: true,
      //   textAnchor: "start",
      //   offsetX: 5,
      //   style: {
      //     fontSize: "10px",
      //     fontWeight: 700,
      //   },
      //   formatter: compactNumber,
      // },

      dataLabels: {
        enabled: true,
        textAnchor: "start",
        offsetX: 6,

        style: {
          fontSize: "12px",
          fontFamily: "Inter, Arial, sans-serif",
          fontWeight: 670,
          colors: ["#ffffff"],
        },

        background: {
          enabled: false,
        },

        dropShadow: {
          enabled: false,
          top: 1,
          left: 1,
          blur: 2,
          opacity: 0.35,
        },

        formatter: compactNumber,
      },

      grid: {
        borderColor: "#eef2f7",
        strokeDashArray: 4,
        padding: {
          left: 4,
          right: 38,
        },
      },

      xaxis: {
        categories: chartData.categories,

        labels: {
          style: {
            fontSize: "11px",
            fontFamily: "Inter, Arial, sans-serif",
            fontWeight: 500,
            colors: "#64748b",
          },
          formatter: compactNumber,
        },

        axisBorder: {
          show: false,
        },

        axisTicks: {
          show: false,
        },
      },

      yaxis: {
        labels: {
          maxWidth: 142,

          style: {
            fontSize: "11px",
            fontFamily: "Inter, Arial, sans-serif",
            fontWeight: 600,
            colors: "#334155",
          },
        },
      },

      tooltip: {
        enabled: true,
        shared: false,
        intersect: true,
        y: {
          title: {
            formatter: () => "Produced: ",
          },
          formatter: (value) => safeNumber(value).toLocaleString("en-IN"),
        },
      },

      noData: {
        text: "No operator production data chart",
        align: "center",
        verticalAlign: "middle",
        style: {
          color: "#94a3b8",
          fontSize: "12px",
        },
      },
    }),
    [chartData.categories, chartKey],
  );

  if (loading) {
    return (
      <div className="operator-production-chart operator-production-chart--loading">
        <div className="operator-chart-loader">
          <span className="operator-chart-spinner" />
          <span>Loading operator production...</span>
        </div>
      </div>
    );
  }

  if (!chartData.categories.length) {
    return (
      <section className="production-dashboard-section">
        <div className="loading-card">
          Loading Operator Production data...
          <Loader isLoading={true} className={"text-center"} />
        </div>
      </section>
      // <div className="operator-production-chart operator-production-chart--empty">
        
      // </div>
    );
  }

  return (
    <div className="operator-production-chart">
      <Chart
        key={animationKey}
        options={options}
        series={series}
        type="bar"
        height={chartHeight}
      />
    </div>
  );
}

export default memo(OperatorProductionChart);
